import produce from "immer";
import { handleActions } from "redux-actions";
import { normalize } from "src/apiService/modules/analyticsGroupings";

import { SystemState } from "../../interfaces";
import {
  coerceStoredCustomToYmdStrings,
  normalizeStoredDateRange,
  shouldUpdateDate,
} from "src/utils/timeFilter";
import { getEffectiveTimeZoneFromUser } from "src/utils/timezone";
import { FAIL, START, SUCCESS } from "../common";
import {
  CLEAR_SIGNUP_STATUS,
  CLEAR_SUBSCRIPTION_RESPONSE,
  FETCH_LOGIN,
  LOGIN_WITH_TOKEN,
  SET_USER,
  LOG_OUT,
  RESET_PASSWORD,
  SIGNUP,
  UPDATE_USER,
  UPDATE_TABLE_COLUMN,
  UPDATE_ANALYTICS_GROUPINGS,
  UPDATE_USER_CLAIMS,
  UPDATE_DATE_RANGE,
  type SetUserPayload,
  UPDATE_THEME_PREFERENCES,
} from "./actions";

const initialState: SystemState = {
  loggedIn: false,
  user: undefined,
  loading: false,
  error: undefined,
  authorizing: false,
  passwordResetSubmitted: false,
  submitting: false,
  submitted: false,
  ebayAuthUrl: "",
  ebayAuthLoading: false,
  ebayAuth: null,
  ebayIntegration: false,
  signupSuccess: false,
  claims: {},
  dashboardDateRangeOptimistic: false,
  transactionDateRangeOptimistic: false,
};

export const systemReducer = handleActions<SystemState, any>(
  {
    [FETCH_LOGIN + START]: (state) =>
      produce(state, (draft) => {
        draft.loading = true;
        draft.error = undefined;
      }),
    [LOGIN_WITH_TOKEN + START]: (state) =>
      produce(state, (draft) => {
        draft.loading = true;
        draft.error = undefined;
      }),
    [FETCH_LOGIN + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loggedIn = true;
        draft.error = undefined;
        draft.user = payload.user;
        draft.authorizing = false;
        draft.claims = payload.claims || {};
      }),
    [FETCH_LOGIN + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loggedIn = false;
        draft.error = payload;
        draft.user = undefined;
      }),
    [SIGNUP + START]: (state) =>
      produce(state, (draft) => {
        draft.loading = true;
        draft.error = undefined;
        draft.authorizing = true;
      }),
    [SIGNUP + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loggedIn = true;
        draft.error = undefined;
        draft.user = payload.user;
        draft.authorizing = false;
        draft.signupSuccess = true;
      }),
    [SIGNUP + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.authorizing = false;
        draft.loading = false;
        draft.loggedIn = false;
        draft.error = payload;
        draft.user = undefined;
      }),
    [SET_USER]: (state, { payload }) =>
      produce(state, (draft) => {
        const isSetUserPayload = (p: typeof payload): p is SetUserPayload =>
          !!p &&
          typeof p === "object" &&
          "user" in p &&
          typeof (p as SetUserPayload).optimisticDateRanges === "boolean";
        const user = isSetUserPayload(payload) ? payload.user : payload;
        const optimisticDateRanges = isSetUserPayload(payload)
          ? payload.optimisticDateRanges
          : false;

        const nextUser = { ...user };

        const tz = getEffectiveTimeZoneFromUser({
          ...draft.user,
          ...nextUser,
        });

        if (optimisticDateRanges) {
          nextUser.dashboardDateRange = coerceStoredCustomToYmdStrings(
            normalizeStoredDateRange(user.dashboardDateRange) ??
              user.dashboardDateRange,
            tz
          );
          nextUser.transactionDateRange = coerceStoredCustomToYmdStrings(
            normalizeStoredDateRange(user.transactionDateRange) ??
              user.transactionDateRange,
            tz
          );
          draft.dashboardDateRangeOptimistic = true;
          draft.transactionDateRangeOptimistic = true;
        } else {
          if (
            shouldUpdateDate(
              user.dashboardDateRange,
              draft.user?.dashboardDateRange,
              tz
            )
          ) {
            nextUser.dashboardDateRange = coerceStoredCustomToYmdStrings(
              normalizeStoredDateRange(user.dashboardDateRange) ??
                user.dashboardDateRange,
              tz
            );
            draft.dashboardDateRangeOptimistic = false;
          } else {
            nextUser.dashboardDateRange = draft.user?.dashboardDateRange;
          }
          if (
            shouldUpdateDate(
              user.transactionDateRange,
              draft.user?.transactionDateRange,
              tz
            )
          ) {
            nextUser.transactionDateRange = coerceStoredCustomToYmdStrings(
              normalizeStoredDateRange(user.transactionDateRange) ??
                user.transactionDateRange,
              tz
            );
            draft.transactionDateRangeOptimistic = false;
          } else {
            nextUser.transactionDateRange = draft.user?.transactionDateRange;
          }
        }

        draft.user = nextUser;
        draft.ebayIntegration = !!user.ebayIntegration;
      }),
    [RESET_PASSWORD + START]: (state) =>
      produce(state, (draft) => {
        draft.loading = true;
      }),
    [RESET_PASSWORD + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.passwordResetSubmitted = true;
      }),
    [RESET_PASSWORD + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),
    [UPDATE_USER + START]: (state) =>
      produce(state, (draft) => {
        draft.loading = true;
        draft.error = false;
      }),
    [UPDATE_USER + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),
    [UPDATE_USER + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = false;
      }),
    [CLEAR_SIGNUP_STATUS]: (state) =>
      produce(state, (draft) => {
        draft.signupSuccess = false;
        draft.error = false;
      }),
    [CLEAR_SUBSCRIPTION_RESPONSE]: (state) =>
      produce(state, (draft) => {
        if (draft.user) {
          draft.user.subscriptionResponse = null;
        }
      }),
    [LOG_OUT]: () => initialState,
    [UPDATE_TABLE_COLUMN]: (state, { payload: { table, config } }) =>
      produce(state, (draft) => {
        if (draft.user) {
          if (!draft.user.tables_configuration)
            draft.user.tables_configuration = {};
          draft.user.tables_configuration[table] = JSON.stringify(config);
        }
      }),
    [UPDATE_ANALYTICS_GROUPINGS]: (state, { payload }) =>
      produce(state, (draft) => {
        if (draft.user?.analytics_groupings) {
          if ("insert" in payload && payload.insert) {
            const department = normalize(payload.insert.department);
            if (!draft.user.analytics_groupings[department])
              draft.user.analytics_groupings[department] = {};
            const category = normalize(payload.insert.category);
            if (!draft.user.analytics_groupings[department][category])
              draft.user.analytics_groupings[department][category] = [];
            const subcategory = normalize(payload.insert.subcategory);
            if (
              !draft.user.analytics_groupings[department][category].includes(
                subcategory
              )
            )
              draft.user.analytics_groupings[department][category].push(
                subcategory
              );
          }

          // TODO: delete
        }
      }),
    [UPDATE_USER_CLAIMS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.claims = payload.claims || {};
      }),

    [UPDATE_DATE_RANGE]: (state, { payload }) =>
      produce(state, (draft) => {
        if (draft.user) {
          if (payload.scope === "dashboard") {
            draft.user.dashboardDateRange = payload.value;
          } else {
            draft.user.transactionDateRange = payload.value;
          }
        }
        if (payload.optimistic === true) {
          if (payload.scope === "dashboard") {
            draft.dashboardDateRangeOptimistic = true;
          } else {
            draft.transactionDateRangeOptimistic = true;
          }
        } else if (payload.optimistic === false) {
          if (payload.scope === "dashboard") {
            draft.dashboardDateRangeOptimistic = false;
          } else {
            draft.transactionDateRangeOptimistic = false;
          }
        }
      }),

    [UPDATE_THEME_PREFERENCES]: (state, { payload }) =>
      produce(state, (draft) => {
        if (draft.user) {
          draft.user.themePreferences = payload;
        }
      }),
  },
  initialState
);
